// Mobx
import { action, computed, observable } from "mobx";
// Api
import { Api } from "../..//api";
// RootStore
import { Store } from "../../store";
// Models
import { Page } from "../../common/interfaces/Page";

import { Icons } from "@mgs/ci2019-common/src/enums/Icons.mercedes";
import moment from "moment";
import { addTokenParameterIfInCurrentUrl } from "../../common/Utils";

export class PageState {
  @observable
  public page?: Page;

  @observable
  public editPermission?: object;

  // RootStore
  public rootStore: Store;

  public constructor(store: Store) {
    this.rootStore = store;
  }

  @action.bound
  public async getPage(url: string, isInEditMode: boolean): Promise<void> {
    this.page = await Api.pageService.getPage(url, isInEditMode);
  }

  @action.bound
  public setEditPermission(permission: object) {
    this.editPermission = permission;
  }

  @computed
  public get pageModules() {
    if (this.page) {
      return this.page.content.slice();
    } else {
      return [];
    }
  }

  @computed
  public get dateString() {
    if (this.page && this.page.basic && this.page.basic.date) {
      let d = moment(this.page.basic.date);
      return d.format("DD.MM.YYYY");
    } else {
      return "";
    }
  }

  @computed
  public get isAllowedToAddModule() {
    return this.editPermission && this.editPermission["add"];
  }

  @computed
  public get isAllowedToEditModule() {
    return this.editPermission && this.editPermission["edit"];
  }

  @computed
  public get isAllowedToDeleteModule() {
    return this.editPermission && this.editPermission["delete"];
  }

  @computed
  public get portalHomeLink() {
    if (this.page) {
      return this.page.portal.home_link;
    } else {
      return "#";
    }
  }

  @computed
  public get portalPiwikId() {
    if (this.page) {
      return this.page.portal.analytics.piwik_id;
    } else {
      return null;
    }
  }

  @computed
  public get headerOptions() {
    if (this.page) {
      return {
        topMenuBefore: [],
        menu: [
          {
            id: "dashboard",
            href: this.portalHomeLink,
            icon: Icons.DASHBOARD,
            text: "Dashboard",
          },
        ],
      };
    } else {
      return {
        topMenuBefore: [],
        menu: [],
      };
    }
  }

  @computed
  public get headerLanguages() {
    if (this.page) {
      return this.page.portal.languages.map((lang, index) => {
        return {
          id: index,
          code: lang.id,
          name: lang.name,
        };
      });
    } else {
      return [];
    }
  }

  @computed
  public get headerRegions() {
    if (this.page) {
      return this.page.portal.countries;
    } else {
      return [];
    }
  }

  @computed
  public get header() {
    if (this.page) {
      return this.page.portal.header;
    } else {
      return null;
    }
  }

  @computed
  public get breadcrumbs() {
    if (this.page) {
      let breadcrumbWithoutHome = this.page.breadcrumb.slice(1);
      return breadcrumbWithoutHome.map((b, index) => {
        return {
          title: b.title.replace("###MODELL###", ""),
          name: addTokenParameterIfInCurrentUrl(b.link),
          active: breadcrumbWithoutHome.length - 1 == index,
        };
      });
    } else {
      return [];
    }
  }

  @computed
  public get breadcrumbHome() {
    if (this.page && this.page.breadcrumb.length > 0) {
      return {
        href: this.page.breadcrumb[0].link,
        active: this.page.breadcrumb.length == 1,
      };
    } else {
      return {
        href: "/",
      };
    }
  }

  @computed
  public get mainNavigation() {
    if (this.page && this.page.main_navigation) {
      return this.page.main_navigation.map((link) => {
        return {
          id: link.id,
          href: link.url,
          target: "_self",
          iconSource: link.icon,
          text: link.title,
          active: link.active,
        };
      });
    } else {
      return [];
    }
  }

  @computed
  public get headerOptionsWithNavigation() {
    if (this.page) {
      return {
        topMenuBefore: [],
        menu: this.mainNavigation,
      };
    } else {
      return {
        topMenuBefore: [],
        menu: [],
      };
    }
  }

  @computed
  public get layoutComponent() {
    if (this.page) {
      if (this.page.design.layout) {
        let layoutName = "Module" + this.page.design.layout;

        return layoutName;
      } else {
        return "ModuleDefaultLayout";
      }
    } else {
      let layoutName = document.body.getAttribute("data-layout");
      if (layoutName) {
        return "Module" + layoutName;
      } else {
        return null;
      }
    }
  }

  public get isInEditMode() {
    return window.location.search.indexOf("mode=edit") >= 0;
  }
}
